import { FC, useState } from 'react'

import { Icons, Progress } from '@agro-club/agroclub-shared'

import { endpoints } from 'modules/endpoints'
import { apiClient } from 'modules/utils/httpClient'

import { useAProgress } from 'hooks/useAProgress'
import { useNotification } from 'hooks/useNotification'
import { useTranslation } from 'react-i18next'
import { AButton } from 'views/components/Analytics'
import ADefaultModalFooter from 'views/components/DefaultModalFooter/ADefaultModalFooter'
import { SharedModalContent, SharedModalTitle } from 'views/components/SharedModal'
import ASharedModal from 'views/components/SharedModal/ASharedModal'
import { AnalyticsPlaces } from 'views/pages/LogisticsKR/analyticsPlaces'
import { TrailerEditModal } from 'views/pages/LogisticsKR/components/TrailerModals/TrailerEditModal'
import { Trailer } from 'views/pages/LogisticsKR/types'
import { DetailsItemButtons, DetailsItemInfo, Text } from 'views/pages/LogisticsKR/components/styled'
import { Card } from 'views/components/Card/Card'
import styled from 'styled-components'
import { TrailerDocuments } from './TrailerDocuments'

interface Props {
  trailer: Trailer
  carrierId: string
  refetch: () => void
}

const SpaceBetween = styled.div`
  display: flex;
  justify-content: space-between;
`

export const TrailerItem: FC<Props> = ({ trailer, carrierId, refetch }) => {
  const { t } = useTranslation('logisticsKR')
  const notify = useNotification()
  const [isModalOpened, setIsModalOpened] = useState(false)
  const [removeConfirmModal, setRemoveConfirmModal] = useState(false)

  const [removeProgress, remove] = useAProgress(
    (id) => apiClient.post(endpoints.LogisticsKR.trailerChangeStatus(id), { new_status: 'archived' }),
    {
      eventName: 'removeTrailer',
      onSuccess: () => {
        refetch()
        notify(Progress.SUCCESS, { title: t('carriers.notifications.trailer_archived_success') })
      },
      onError: () => notify(Progress.ERROR, { title: t('carriers.notifications.trailer_archived_error') }),
    },
  )

  const trailerParams = [
    !!trailer.brand_model && trailer.brand_model,
    !!trailer.unloading_type && t(`directions.${trailer.unloading_type}`),
    !!trailer.side_height && t('common:number_m', { number: trailer.side_height }),
  ]
    .filter(Boolean)
    .join(', ')

  return (
    <>
      <Card.Container style={{ minHeight: '110px' }}>
        <SpaceBetween>
          <DetailsItemInfo>
            <Text>{trailer.licence_number}</Text>
            <Text>{trailerParams}</Text>
          </DetailsItemInfo>
          <DetailsItemButtons>
            <AButton
              id="editTrailer"
              color="secondary"
              background="none"
              Icon={Icons.IconEdit}
              onClick={() => setIsModalOpened(true)}
            />
            <AButton
              id="removeTrailer"
              color="secondary"
              background="none"
              Icon={Icons.IconDelete}
              onClick={() => setRemoveConfirmModal(true)}
            />
          </DetailsItemButtons>
        </SpaceBetween>
        <TrailerDocuments trailerId={trailer.id.toString()} />
      </Card.Container>

      {isModalOpened && (
        <TrailerEditModal trailer={trailer} setIsOpen={setIsModalOpened} carrierId={carrierId} onSuccess={refetch} />
      )}
      {removeConfirmModal && (
        <ASharedModal
          id={AnalyticsPlaces.CARRIERS.DELETE_TRAILER_MODAL}
          size="small"
          onClose={() => setRemoveConfirmModal(false)}
        >
          <SharedModalTitle>{t('carriers.confirmModals.archivedTrailer')}</SharedModalTitle>
          <SharedModalContent>{t('carriers.confirmModals.youReallyWantArchivedTrailer')}</SharedModalContent>
          <ADefaultModalFooter
            onClose={() => setRemoveConfirmModal(false)}
            onConfirm={() => remove(trailer.id)}
            progress={removeProgress}
          />
        </ASharedModal>
      )}
    </>
  )
}
